import React, { createContext, useContext, useCallback, useState, useRef, useEffect } from 'react';

const ToastContext = createContext(null);

const TOAST_DURATION = 3000;
const EXIT_DURATION = 400;
const MAX_VISIBLE = 3;

let toastId = 0;

const ICONS = {
  success: 'check_circle',
  error: 'error_outline',
  info: 'info',
};

function ToastItem({ toast, onDismiss }) {
  const [leaving, setLeaving] = useState(false);
  const timerRef = useRef(null);

  const startExit = useCallback(() => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(() => onDismiss(toast.id), EXIT_DURATION);
  }, [leaving, onDismiss, toast.id]);

  useEffect(() => {
    timerRef.current = setTimeout(startExit, TOAST_DURATION);
    return () => clearTimeout(timerRef.current);
  }, [startExit]);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(timerRef.current);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timerRef.current = setTimeout(startExit, 2000);
  }, [startExit]);

  return (
    <div
      className={`ws-toast ws-toast--${toast.type} ${leaving ? 'ws-toast--leaving' : ''}`}
      role="status"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className="ws-toast__icon material-icons">{ICONS[toast.type]}</span>
      <span className="ws-toast__message">{toast.message}</span>
      <button
        type="button"
        className="ws-toast__close"
        onClick={startExit}
        aria-label="Sluiten"
      >
        <span className="material-icons">close</span>
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((message, type = 'info') => {
    const id = ++toastId;
    setToasts(prev => {
      const next = prev.length >= MAX_VISIBLE ? prev.slice(1) : prev;
      return [...next, { id, message, type }];
    });
    return id;
  }, []);

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      <div className="ws-toast-container" aria-live="polite" aria-atomic="false">
        {toasts.map(toast => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
